<template>
  <div class="login-container">
    <div class="login-card">
      <div class="logo-container">
        <img src="/logo.png" alt="JLHVISION" class="login-logo">
        <h2 class="login-title">Iniciar Sesión</h2>
      </div>
      
      <form @submit.prevent="onSubmit">
        <div class="form-group">
          <input 
            type="email" 
            v-model="form.email" 
            placeholder="Correo electrónico"
            :class="{ 'is-invalid': errors.email }"
            @input="clearError('email')"
          >
          <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <div class="password-wrapper">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              v-model="form.password" 
              placeholder="Contraseña"
              :class="{ 'is-invalid': errors.password }"
              @input="clearError('password')"
            >
            <button type="button" class="toggle-password" @click="togglePasswordVisibility">
              <i class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
          <span class="error-message" v-if="errors.password">{{ errors.password }}</span>
        </div>

        <button type="submit" :disabled="loading" class="btn-login">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Iniciando...' : 'Iniciar Sesión' }}
        </button>

        <div class="links">
          <router-link to="/forgot-password" class="link">¿Olvidaste tu contraseña?</router-link>
        </div>

        <div class="register-link">
          ¿No tienes cuenta? <router-link to="/register" class="link register">Crea una ahora</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'

const router = useRouter()
const loading = ref(false)
const showPassword = ref(false)

const form = reactive({
  email: '',
  password: ''
})

const errors = reactive({
  email: '',
  password: ''
})

const clearError = (field) => {
  errors[field] = ''
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const onSubmit = async () => {
  // Limpiar errores
  errors.email = ''
  errors.password = ''
  
  // Validaciones básicas
  if (!form.email) {
    errors.email = 'El correo electrónico es requerido'
    return
  }
  if (!form.password) {
    errors.password = 'La contraseña es requerida'
    return
  }
  
  loading.value = true
  
  try {
    const response = await api.post('/app/login', {
      email: form.email,
      password: form.password
    })
    
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.user))
      router.push('/home')
    }
  } catch (error) {
    if (error.response?.data?.errors) {
      const err = error.response.data.errors
      if (err.email) errors.email = err.email[0]
      if (err.password) errors.password = err.password[0]
    } else if (error.response?.data?.message) {
      errors.password = error.response.data.message
    } else {
      errors.password = 'Error al iniciar sesión'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.login-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.login-card {
  background: white;
  padding: 40px 30px;
  width: 100%;
  max-width: 400px;
}

.logo-container {
  text-align: center;
  margin-bottom: 30px;
}

.login-logo {
  width: 100px;
  height: auto;
  object-fit: contain;
}

.login-title {
  text-align: center;
  color: #00144a;
  font-size: 1.5rem;
  font-weight: bold;
  margin-top: 15px;
}

.form-group {
  margin-bottom: 20px;
}

input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s;
  background: #f9f9f9;
}

input:focus {
  outline: none;
  border-color: #00144a;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 20, 74, 0.1);
}

input.is-invalid {
  border-color: #f12711;
  background: rgba(241, 39, 17, 0.05);
}

.error-message {
  display: block;
  color: #f12711;
  font-size: 12px;
  margin-top: 5px;
}

.password-wrapper {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 0;
}

.toggle-password i {
  font-size: 20px;
}

.toggle-password:hover {
  color: #00144a;
}

.btn-login {
  width: 100%;
  padding: 14px;
  background: #00144a;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-login:hover {
  background: #002a73;
  transform: translateY(-2px);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.links {
  text-align: center;
  margin-top: 20px;
}

.link {
  color: #a50000;
  text-decoration: none;
  font-size: 14px;
}

.link:hover {
  text-decoration: underline;
}

.register-link {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #666;
}

.register-link .register {
  color: #00144a;
  font-weight: 600;
}

@media (max-width: 480px) {
  .login-card {
    padding: 30px 20px;
  }
  
  .login-logo {
    width: 80px;
  }
  
  input {
    padding: 12px 14px;
    font-size: 14px;
  }
  
  .btn-login {
    padding: 12px;
  }
}
</style>